/* A case study's photos where they sit in the draft.
 *
 * The draft editor shows the gallery block as a placeholder it puts back
 * untouched on save, because the grid's HTML is not the draft's to edit:
 * withCaseStudyPhotos rebuilds it from the photo rows on every change and on
 * every push. So this shows the rows themselves, and ordering or opening one
 * here is the same as doing it in the Job photos panel (same query, same
 * routes) — the HTML follows from the server, never from this.
 *
 * Rendered by HtmlDraft into its placeholder; the settings window goes to
 * <body>, out of the article, whose own styles and editing handlers would
 * otherwise reach into it. */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/lib/icons';
import { getArticle, reorderArticlePhotos, setPhotoFile } from '@/data/client';
import { AssetImg } from './AssetImg';
import { DragGrip, useMoveOrder } from './MoveButtons';
import { PhotoSettings } from './PhotoSet';
import { useDragOrder } from './useDragOrder';

export function DraftGallery({ articleId }: { articleId: string }) {
  const qc = useQueryClient();
  // CaseStudyPhotos' key and shape, so the panel and the draft are one cache.
  const queryKey = ['article', articleId, 'photos'];
  const { data } = useQuery({
    queryKey,
    queryFn: async () => {
      const a = await getArticle(articleId);
      return { photos: a.photos, mainId: a.heroPhotoId };
    },
  });
  const [settingsId, setSettingsId] = useState('');
  const [err, setErr] = useState('');

  const photos = data?.photos ?? [];
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey });
    void qc.invalidateQueries({ queryKey: ['article', articleId], exact: true });
  };
  const keys = photos.map((p) => p.id);
  const reorder = (ids: string[]) => reorderArticlePhotos(articleId, ids);
  const order = useMoveOrder(keys, reorder, { onSaved: refresh, onError: setErr });
  const drag = useDragOrder(keys, reorder, { onSaved: refresh, onError: setErr });
  const byId = new Map(photos.map((p) => [p.id, p]));
  const shown = drag.ids.map((k) => byId.get(k)).filter((p) => !!p);
  const settings = settingsId ? byId.get(settingsId) : undefined;

  if (!data) return <span className="cq-dgal__note">Loading photos…</span>;
  if (!photos.length) return <span className="cq-dgal__note">The gallery goes here. Add photos in Job photos.</span>;

  return (
    <div className="cq-dgal">
      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
      <div className={`cq-dgal__grid${drag.draggingId ? ' is-dragging' : ''}`}>
        {shown.map((p) => (
          <div key={p.id} ref={(el) => drag.register(p.id, el)} title={p.caption || p.alt || ''}
            className={`cq-dgal__tile${drag.draggingId === p.id ? ' is-held' : ''}`}>
            {p.isVideo
              ? <video className="cq-dgal__img" src={`${p.cdnUrl}#t=0.1`} muted playsInline preload="metadata" />
              : <AssetImg url={p.cdnUrl} assetId={p.assetId} alt={p.alt || p.filename} className="cq-dgal__img" />}
            {photos.length > 1 && <DragGrip id={p.id} drag={drag} keys={order} label="photo" className="es-grip cq-dgal__grip" />}
            <button type="button" className="cq-dgal__gear" title="Photo settings: file name, caption, alt text, album"
              aria-label="Photo settings" onClick={() => { setSettingsId(p.id); setErr(''); }}>
              <Icon name="settings" size={14} />
            </button>
          </div>
        ))}
      </div>
      {settings && createPortal(
        <PhotoSettings key={settings.id} photo={settings} editFile={(id, input) => setPhotoFile(id, input)}
          onClose={() => setSettingsId('')} onSaved={() => { void refresh(); }} />,
        document.body,
      )}
    </div>
  );
}
